import { alpha, createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';
import type { } from '@mui/material/themeCssVarsAugmentation';

// Color palette based on bright orange logo
const orangePalette = {
  50: '#fff7ed',
  100: '#ffedd5',
  200: '#fed7aa',
  300: '#fdba74',
  400: '#fb923c',
  500: '#f97316', // Main orange
  600: '#ea580c',
  700: '#c2410c',
  800: '#9a3412',
  900: '#7c2d12',
};

// Enhanced neutral colors for off-white and backgrounds
const neutralPalette = {
  50: '#fafaf9',
  100: '#f5f5f4',
  200: '#e7e5e4',
  300: '#d6d3d1',
  400: '#a8a29e',
  500: '#78716c',
  600: '#57534e',
  700: '#44403c',
  800: '#292524',
  900: '#1c1917',
};

// Export color arrays for easy access
export const PRIMARY_COLOR = {
  rgb: '249, 115, 22', // orangePalette[500] as RGB for rgba usage
  hex: '#f97316',
  palette: orangePalette,
};

export const SECONDARY_COLOR = {
  rgb: '99, 102, 241', // indigo as RGB for rgba usage
  hex: '#6366f1',
};


// Create light theme palette
const lightPalette = {
  primary: {
    main: orangePalette[500],
    light: orangePalette[300],
    dark: orangePalette[700],
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#6366f1', // Complementary indigo
    light: '#818cf8',
    dark: '#4f46e5',
    contrastText: '#ffffff',
  },
  background: {
    default: neutralPalette[50],
    paper: '#ffffff',
    card: neutralPalette[100],
  },
  text: {
    primary: neutralPalette[800],
    secondary: neutralPalette[600],
    disabled: neutralPalette[400],
  },
  grey: neutralPalette,
  divider: neutralPalette[200],
  action: {
    hover: alpha(orangePalette[500], 0.04),
    selected: alpha(orangePalette[500], 0.08),
    disabled: neutralPalette[400],
    disabledBackground: neutralPalette[100],
  },
};

// Create dark theme palette
const darkPalette = {
  primary: {
    main: orangePalette[400],
    light: orangePalette[300],
    dark: orangePalette[600],
    contrastText: neutralPalette[900],
  },
  secondary: {
    main: '#818cf8', // Lighter indigo for dark mode
    light: '#a5b4fc',
    dark: '#6366f1',
    contrastText: neutralPalette[900],
  },
  background: {
    default: '#0f0f0f',
    paper: '#1a1a1a',
    card: neutralPalette[800],

  },
  text: {
    primary: neutralPalette[100],
    secondary: neutralPalette[300],
    disabled: neutralPalette[500],
  },
  grey: {
    50: neutralPalette[900],
    100: neutralPalette[800],
    200: neutralPalette[700],
    300: neutralPalette[600],
    400: neutralPalette[500],
    500: neutralPalette[400],
    600: neutralPalette[300],
    700: neutralPalette[200],
    800: neutralPalette[100],
    900: neutralPalette[50],
  },
  divider: neutralPalette[700],
  action: {
    hover: alpha(orangePalette[400], 0.08),
    selected: alpha(orangePalette[400], 0.12),
    disabled: neutralPalette[600],
    disabledBackground: neutralPalette[800],
  },
};

const colorSchemes = {
  light: {
    palette: lightPalette,
  },
  dark: {
    palette: darkPalette,
  }
}


// Common theme options for both light and dark
const commonThemeOptions: ThemeOptions = {
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme: Theme) => ({
        body: {
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
        },
        html: {
          backgroundColor: theme.palette.background.default,
        },
        '#root': {
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
        },
      }),
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '8px',
          [theme.breakpoints.up('md')]: {
            padding: '10px 24px',
          },
          fontSize: '0.95rem',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        }),
        text: ({ theme, ownerState }: { theme: Theme, ownerState: any }) => ({
          color: ownerState.color === 'secondary'
            ? theme.palette.secondary.main
            : theme.palette.primary.main,
          '&:hover': {
            backgroundColor: ownerState.color === 'secondary'
              ? theme.palette.secondary.main + '14'
              : theme.palette.primary.main + '14',
            color: ownerState.color === 'secondary'
              ? theme.palette.secondary.dark
              : theme.palette.primary.dark,
          },
        }),
        contained: ({ theme, ownerState }: { theme: Theme, ownerState: any }) => {
          const color =
            ownerState.color === 'secondary'
              ? theme.palette.secondary
              : theme.palette.primary;
          return {
            backgroundColor: color.main,
            color: color.contrastText,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: color.dark,
              color: color.contrastText,
              boxShadow: 'none',
              transform: 'translateY(-1px)',
              transition: 'all 0.2s ease-in-out',
            },
            '&:active': {
              backgroundColor: color.dark,
              color: color.contrastText,
            },
            '&.Mui-disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
            },
          };
        },
        outlined: ({ theme, ownerState }: { theme: Theme, ownerState: any }) => {
          const color =
            ownerState.color === 'secondary'
              ? theme.palette.secondary
              : theme.palette.primary;
          return {
            borderWidth: '2px',
            borderColor: color.main,
            color: color.main,
            backgroundColor: 'transparent',
            '&:hover': {
              borderWidth: '2px',
              borderColor: color.dark,
              color: color.dark,
              backgroundColor: color.main + '08',
            },
            '&.Mui-disabled': {
              borderColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
            },
          };
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.875rem',
        },
        sizeLarge: {
          padding: '12px 32px',
          fontSize: '1.05rem',
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: 16,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid',
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ([{
          borderRadius: 12,
          backgroundColor: '#fff',
        }, theme.applyStyles('dark', {
          backgroundColor: '#1a1a1a',
        })]),
      },
      defaultProps: {
        elevation: 0,
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }: { theme: Theme }) => ({
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderRight: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ([
          {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
              backgroundColor: theme.palette.background.paper,
              '& fieldset': {
                borderWidth: '2px',
                borderColor: neutralPalette[700],
              },
              '&:hover fieldset': {
                borderWidth: '2px',
                borderColor: neutralPalette[800],
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
                borderColor: neutralPalette[900],
              },
            },
          }, theme.applyStyles('dark', {
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#1a1a1a',
              color: neutralPalette[100],
              '& fieldset': {
                borderWidth: '2px',
                borderColor: neutralPalette[500],
              },
              '&:hover fieldset': {
                borderWidth: '2px',
                borderColor: neutralPalette[400],
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
                borderColor: neutralPalette[300],
              },
            },

          })
        ]),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: 8,
          fontWeight: 500,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }: { theme: Theme }) => ({
          borderRadius: 20,
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.95rem',
          borderRadius: 8,
          margin: '0 4px',
          minHeight: 40,
          color: 'text.primary',
          '&.Mui-selected': {
            fontWeight: 700,
            color: 'primary.main'
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: 2,
            backgroundColor: 'primary.main',
          },
        }),
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: 8,
          margin: '2px 0',
          '&.Mui-selected': {
            borderRadius: 8,
            backgroundColor: 'action.selected',
          },
        }),
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          fontWeight: 600,
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
        }),
      },
    },
    MuiIconButton: {
      defaultProps: {
        sx: {
          color: 'text.primary',
        }
      },
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: 10,
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }: { theme: Theme }) => ({
          borderRadius: 12,
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          border: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: 8,
          margin: '2px 8px',
          color: 'text.primary',
          '&:hover': {
            borderRadius: 8,
            backgroundColor: 'action.hover',
          },
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }: { theme: Theme }) => ([
          {
            backgroundColor: '#FFFFFF',
            color: theme.palette.text.primary,
            fontSize: '0.75rem',
            borderRadius: 8,
            padding: '8px 12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: `1px solid ${theme.palette.divider}`,
          },
          theme.applyStyles('dark', {
            backgroundColor: theme.palette.grey[800],
            color: theme.palette.grey[100],
            border: `1px solid ${theme.palette.grey[700]}`,
          }),
        ]),
        arrow: ({ theme }: { theme: Theme }) => ([
          {
            color: '#FFFFFF',
            '&::before': {
              border: `1px solid ${theme.palette.divider}`,
            },
          },
          theme.applyStyles('dark', {
            color: theme.palette.grey[800],
            '&::before': {
              border: `1px solid ${theme.palette.grey[700]}`,
            },
          }),
        ]),
      },
    },
    MuiTypography: {
      defaultProps: {
        color: 'text.primary',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          backgroundColor: 'background.paper',
          color: 'text.primary',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'divider',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'text.secondary',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
          },
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          color: 'text.secondary',
          '&.Mui-focused': {
            color: 'primary.main',
          },
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          color: 'text.secondary',
        }),
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          color: 'primary.main',
          '&:hover': {
            color: 'primary.dark',
          },
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ([
          {
            borderRadius: 12,
            color: 'text.primary',
            border: `1px solid divider`,
            '& .MuiIconButton-root': {
              color: 'inherit',
            },
          },
          // Dark mode overrides for each severity
          theme.applyStyles('dark', {
            backgroundColor: 'background.paper',
            color: 'text.primary',
            '&.MuiAlert-standardError, &.MuiAlert-filledError, &.MuiAlert-outlinedError': {
              backgroundColor: 'error.dark',
              color: 'error.contrastText',
              borderColor: 'error.main',
            },
            '&.MuiAlert-standardWarning, &.MuiAlert-filledWarning, &.MuiAlert-outlinedWarning': {
              backgroundColor: 'warning.dark',
              color: 'warning.contrastText',
              borderColor: 'warning.main',
            },
            '&.MuiAlert-standardInfo, &.MuiAlert-filledInfo, &.MuiAlert-outlinedInfo': {
              backgroundColor: 'info.dark',
              color: 'info.contrastText',
              borderColor: 'info.main',
            },
            '&.MuiAlert-standardSuccess, &.MuiAlert-filledSuccess, &.MuiAlert-outlinedSuccess': {
              backgroundColor: 'success.dark',
              color: 'success.contrastText',
              borderColor: 'success.main',
            },
          }),
        ]),
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
        }),
      },
    },
  },
  typography: {
    fontFamily: '"Inter", "Nunito Sans", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.4,
      letterSpacing: '-0.005em',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.95rem',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.4,
    },
  },
  shape: {
    borderRadius: 12,
  },
  spacing: 8,
};

const cssVarsTheme = createTheme({
  ...commonThemeOptions,
  cssVariables: {
    colorSchemeSelector: 'class'
  },
  colorSchemes,
});

export default cssVarsTheme;